/**
 * First-touch acquisition stash for signup attribution.
 *
 * Writes a small, first-party, keep-first acquisition record on page load so
 * the backend can attribute a later signup to the surface, SEO dimension and
 * traffic channel that brought the visitor in. Pure client-side write: no
 * origin Set-Cookie is involved, so this survives CDN cache hits on the
 * directory pages (the cached HTML carries no per-visitor state, the cookie is
 * written by this script in the browser).
 *
 * The stash is read SERVER-SIDE at signup intake (see
 * Interpals\Domain\Service\Seo\SignupAttributionService), which is why this
 * module persists to a cookie (sent on every request) plus a localStorage
 * mirror (resilience against cookie loss across the multi-step funnel).
 *
 * Keep-first: an existing, non-expired stash is never overwritten, so the
 * original entry surface wins even if the visitor later lands on the homepage
 * before signing up.
 *
 * Privacy:
 * - referrer_host is the eTLD+1 of document.referrer only (never the full
 *   referrer URL or its query string).
 * - landing_path is the path of the first landing page. The query string is
 *   always dropped.
 * - No identifier of any kind is placed in a URL.
 */

import { safeStorageGet, safeStorageRemove, safeStorageSet } from './safe-storage.js';

// Cookie name. Mirrors the PHP constant
// Interpals\Domain\Service\Seo\SignupAttributionService::STASH_COOKIE_NAME —
// keep the two in sync.
const STASH_COOKIE_NAME = 'ip_acq';

// First-touch window. A stash older than this is treated as absent and
// rewritten, so a returning visitor is re-attributed rather than credited to a
// stale month-old touch. Matches
// SignupAttributionService::STASH_MAX_AGE_SECONDS.
const STASH_MAX_AGE_DAYS = 30;
const STASH_MAX_AGE_SECONDS = STASH_MAX_AGE_DAYS * 24 * 60 * 60;

// localStorage mirror key (cookie loss resilience across the funnel).
const STASH_STORAGE_KEY = 'ip/acq-stash';

// Directory path pattern. Mirrors the server-side guard in
// SignupAttributionService.
const DIRECTORY_PATH_RE = /^\/(language|country)\//i;

// UTM parameters kept in the stash. The server bounds each one again.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Invite or referral value. Mirrors the charset and length the server accepts
// (SignupAttribution::REF_SCHEMA_PATTERN, SignupAttribution::MAX_REF_LEN).
const REF_RE = /^[A-Za-z0-9._-]{1,64}$/;
const MAX_LANDING_PATH_LEN = 255;
const MAX_UTM_LEN = 128;
const MAX_GCLID_LEN = 128;

// Cookie bytes per utm or gclid value, after JSON escaping and URL encoding.
// 128 characters of 3-byte UTF-8 fit.
const MAX_VALUE_ENCODED_LEN = 1152;

// The cookie goes with every request to the domain, so it stays well under the 4096-byte browser limit.
const MAX_COOKIE_VALUE_LEN = 2048;

// Dropped in this order until the cookie fits.
const SHED_ORDER = ['gclid', 'utm_term', 'utm_content', 'utm_campaign', 'utm_medium', 'utm_source'];

// A client clock may run this far ahead of the server. Matches SignupAttribution::MAX_CLOCK_SKEW_SECONDS.
const MAX_CLOCK_SKEW_SECONDS = 86400;

/**
 * eTLD+1-ish registrable domain of a referrer host. We deliberately keep this
 * to the last two labels (e.g. "www.google.co.uk" -> "google.co.uk" is not
 * attempted, it yields "co.uk") because a full public-suffix list is overkill
 * here: the server-side classifier matches search engines by a leading-label
 * allow-list ("google.", "bing.com", ...), so a coarse host is enough and
 * keeps PII minimal. Returns '' for same-origin or empty referrers.
 */
function referrerHost(referrer, currentHost) {
    if (!referrer) {
        return '';
    }
    let host;
    try {
        host = new URL(referrer).hostname.toLowerCase();
    } catch (_) {
        return '';
    }
    if (!host || host === currentHost) {
        return '';
    }
    host = host.replace(/^www\./, '');
    const labels = host.split('.');
    if (labels.length <= 2) {
        return host;
    }
    // Keep the last three labels for multi-part TLDs (co.uk, com.br), the last
    // two otherwise. This is a heuristic, not a public-suffix lookup.
    const secondLevel = labels[labels.length - 2];
    const isMultiPartTld = secondLevel.length <= 3;
    return labels.slice(isMultiPartTld ? -3 : -2).join('.');
}

/**
 * Reads the directory dimension the page rendered into the data island, if
 * present. The directory templates emit a
 * <script type="application/json" id="ip-acq-page"> block with page_type and
 * seo_keys so this survives CDN caching (it is part of the cached HTML).
 */
function readPageDimension() {
    const el = document.getElementById('ip-acq-page');
    if (!el) {
        return null;
    }
    try {
        const data = JSON.parse(el.textContent || '{}');
        if (!data || typeof data.page_type !== 'string') {
            return null;
        }
        return {
            page_type: data.page_type,
            seo_keys: Array.isArray(data.seo_keys)
                ? data.seo_keys.filter((k) => typeof k === 'string' && k !== '')
                : [],
        };
    } catch (_) {
        return null;
    }
}

/**
 * The value cut to maxLen characters and to its cookie budget, or null when empty.
 * Code points, not UTF-16 units: a split surrogate pair makes encodeURIComponent throw.
 */
function capValue(value, maxLen) {
    let chars = Array.from(value || '').slice(0, maxLen);
    while (encodeURIComponent(JSON.stringify(chars.join('')).slice(1, -1)).length > MAX_VALUE_ENCODED_LEN) {
        chars = chars.slice(0, -8);
    }
    return chars.length > 0 ? chars.join('') : null;
}

function readUtm(params) {
    const utm = {};
    UTM_KEYS.forEach((key) => {
        // Cap each value defensively — query strings are attacker-controlled.
        const value = capValue(params.get(key), MAX_UTM_LEN);
        if (value !== null) {
            utm[key] = value;
        }
    });
    return utm;
}

/** The `ref` query value, or null when it leaves the accepted charset. */
function readRef(params) {
    const value = (params.get('ref') || '').trim();
    return REF_RE.test(value) ? value : null;
}

/**
 * The stash if it exists and is within the first-touch window, or null.
 */
function freshStash(parsed) {
    if (!parsed) {
        return null;
    }
    const firstTouchAt = Number(parsed.first_touch_at) || 0;
    if (!firstTouchAt) {
        return null;
    }
    const ageSeconds = Math.floor(Date.now() / 1000) - firstTouchAt;
    if (ageSeconds < -MAX_CLOCK_SKEW_SECONDS || ageSeconds > STASH_MAX_AGE_SECONDS) {
        return null;
    }
    return parsed;
}

/** The cookie holds URL-encoded JSON. The localStorage mirror holds plain JSON. */
function parseStash(raw, urlEncoded) {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(urlEncoded ? decodeURIComponent(raw) : raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
        return null;
    }
}

function readCookie(name) {
    const match = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
    );
    return match ? match[1] : null;
}

/**
 * Registrable cookie domain so the stash is shared across www / apex / api
 * subdomains and survives the OAuth bounce. Mirrors PHP Core\Cookie's leading-
 * dot apex scoping. Falls back to host-only on unusual hostnames.
 */
function cookieDomain(hostname) {
    if (!hostname || /^[0-9.]+$/.test(hostname) || hostname === 'localhost') {
        return '';
    }
    const labels = hostname.replace(/^(www|local)\./, '').split('.');
    if (labels.length < 2) {
        return '';
    }
    const secondLevel = labels[labels.length - 2];
    const isMultiPartTld = secondLevel.length <= 3 && labels.length >= 3;
    return '.' + labels.slice(isMultiPartTld ? -3 : -2).join('.');
}

function cookieAttributes(maxAge) {
    const domain = cookieDomain(window.location.hostname);
    const parts = ['path=/', 'max-age=' + maxAge, 'SameSite=Lax'];
    if (domain) {
        parts.push('domain=' + domain);
    }
    if (window.location.protocol === 'https:') {
        parts.push('Secure');
    }
    return parts;
}

function clearStash() {
    if (readCookie(STASH_COOKIE_NAME) !== null) {
        document.cookie = [STASH_COOKIE_NAME + '='].concat(cookieAttributes(0)).join('; ');
        // A host-only copy survives a clear that names the domain.
        document.cookie = STASH_COOKIE_NAME + '=; path=/; max-age=0';
    }
    if (safeStorageGet(STASH_STORAGE_KEY) !== null) {
        safeStorageRemove(STASH_STORAGE_KEY);
    }
}

function writeStash(stash) {
    let value = encodeURIComponent(JSON.stringify(stash));
    for (const field of SHED_ORDER) {
        if (value.length <= MAX_COOKIE_VALUE_LEN) {
            break;
        }
        stash = field === 'gclid' ? { ...stash, gclid: null } : { ...stash, utm: { ...stash.utm } };
        if (field !== 'gclid') {
            delete stash.utm[field];
        }
        value = encodeURIComponent(JSON.stringify(stash));
    }

    document.cookie = [STASH_COOKIE_NAME + '=' + value].concat(cookieAttributes(STASH_MAX_AGE_SECONDS)).join('; ');

    safeStorageSet(STASH_STORAGE_KEY, JSON.stringify(stash));
}

function captureAcquisition() {
    try {
        // A member's visit is not an acquisition, and a signed-in page follows the signup that
        // used the stash. head.twig sets userId for a signed-in page.
        if (window.userId) {
            clearStash();
            return;
        }
        if (freshStash(parseStash(readCookie(STASH_COOKIE_NAME), true))) {
            return; // keep-first
        }
        // The server reads only the cookie, so a first touch kept in the mirror goes back into it.
        const mirrored = freshStash(parseStash(safeStorageGet(STASH_STORAGE_KEY), false));
        if (mirrored) {
            writeStash(mirrored);
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const dimension = readPageDimension();
        const path = window.location.pathname;
        const isDirectoryLanding = DIRECTORY_PATH_RE.test(path);

        const stash = {
            surface: isDirectoryLanding ? 'directory' : 'site',
            page_type: dimension ? dimension.page_type : null,
            seo_keys: dimension ? dimension.seo_keys : [],
            // Path only, no query. The cut never splits a %XX escape.
            landing_path: path.slice(0, MAX_LANDING_PATH_LEN).replace(/%[0-9A-Fa-f]?$/, ''),
            referrer_host: referrerHost(document.referrer, window.location.hostname),
            utm: readUtm(params),
            ref: readRef(params),
            gclid: capValue(params.get('gclid'), MAX_GCLID_LEN),
            first_touch_at: Math.floor(Date.now() / 1000),
        };

        writeStash(stash);
    } catch (err) {
        // Attribution is best-effort and must never affect the page.
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
    }
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', captureAcquisition);
    } else {
        captureAcquisition();
    }
}

export { referrerHost, cookieDomain, captureAcquisition, readRef, readUtm };
